import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

const PURCHASE_OPTION_SELECTOR =
  'input[name="selling_plan"], [data-radio-type], [class*="subscription" i], [class*="appstle" i]';

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {Element | null} */
  #footer = null;

  /** @type {Element | null} */
  #observedTarget = null;

  /** @type {MediaQueryList} */
  #belowDesktopQuery = window.matchMedia('(max-width: 989px)');

  /** @type {number} */
  #planSyncFrame = 0;

  /** @type {boolean} */
  #planSyncAnnounce = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#syncStickyFlag();

    const { signal } = this.#abortController;
    this.#belowDesktopQuery.addEventListener('change', this.#handleBreakpointChange, { signal });
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener('change', this.#handlePurchaseOptionChange, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#syncSellingPlan();
    if (document.readyState === 'complete') {
      requestAnimationFrame(() => this.#syncSellingPlan());
    } else {
      window.addEventListener('load', () => this.#syncSellingPlan(), { once: true, signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    delete document.documentElement.dataset.caStickyAtc;
    delete document.documentElement.dataset.caStickyAtcAvailable;
    this.#observedTarget = null;
    if (this.#planSyncFrame) cancelAnimationFrame(this.#planSyncFrame);
    this.#planSyncFrame = 0;
    this.#planSyncAnnounce = false;
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = /** @type {HTMLElement | null} */ (productForm.closest('.buy-buttons-block'));
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsBlock = buyButtonsBlock;
    this.#footer = footer;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries.findLast((e) => e.target === this.#observedTarget);
        if (!entry) return;

        if (this.#isBelowDesktop()) {
          if (entry.intersectionRatio >= 0.5) {
            this.#hiddenByBottom = false;
            this.#hideStickyBar();
          } else if (!this.#hiddenByBottom) {
            this.#showStickyBar();
          }
          return;
        }

        // Desktop: only show sticky bar if buy buttons have been scrolled past (above viewport)
        if (!entry.isIntersecting && !this.#isStuck) {
          // Check if the element is above the viewport (scrolled past) or below (not yet reached)
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            // Element is above viewport - show sticky bar
            this.#showStickyBar();
          }
          // If rect.top >= 0, element is below viewport - don't show sticky bar yet
        } else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
        }
      },
      { threshold: [0, 0.5] }
    );

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          const inlineOutOfView = this.#isBelowDesktop()
            ? !this.#isTargetInView()
            : rect.bottom < 0 || rect.top < 0;
          if (inlineOutOfView) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#observeTarget();
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    if (this.#isBelowDesktop()) {
      this.#syncVisibility();
    }
  }

  /**
   * Observes the inline ATC button below desktop, otherwise the whole buy-buttons block.
   */
  #observeTarget() {
    const observer = this.#buyButtonsIntersectionObserver;
    const block = this.#buyButtonsBlock;
    if (!observer || !block) return;

    const button = this.#isBelowDesktop() ? this.#getProductForm()?.querySelector('[ref="addToCartButton"]') : null;
    const next = button ?? block;
    if (next === this.#observedTarget) return;

    if (this.#observedTarget) observer.unobserve(this.#observedTarget);
    this.#observedTarget = next;
    observer.observe(next);
  }

  /**
   * Whether at least half of the observed inline target is inside the viewport.
   * @returns {boolean}
   */
  #isTargetInView() {
    const target = this.#observedTarget;
    if (!target || !target.isConnected) return false;
    const rect = target.getBoundingClientRect();
    if (rect.height === 0) return false;
    const visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    return visible / rect.height >= 0.5;
  }

  /**
   * Recomputes sticky bar visibility from current layout.
   */
  #syncVisibility() {
    const target = this.#observedTarget;
    if (!target) return;

    const footerRect = this.#footer?.getBoundingClientRect();
    const nearFooter = !!footerRect && footerRect.top < window.innerHeight && footerRect.bottom > -200;

    if (this.#isBelowDesktop()) {
      if (this.#isTargetInView()) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      } else if (nearFooter) {
        this.#hiddenByBottom = true;
        this.#hideStickyBar();
      } else {
        this.#hiddenByBottom = false;
        this.#showStickyBar();
      }
      return;
    }

    const rect = target.getBoundingClientRect();
    const intersecting = rect.bottom > 0 && rect.top < window.innerHeight;
    if (!intersecting && rect.top < 0 && !nearFooter) {
      this.#hiddenByBottom = false;
      this.#showStickyBar();
    } else {
      this.#hiddenByBottom = !intersecting && nearFooter;
      this.#hideStickyBar();
    }
  }

  #handleBreakpointChange = () => {
    this.#observeTarget();
    this.#syncVisibility();
  };

  /**
   * Whether the viewport is below the desktop layout (≤989px).
   * @returns {boolean}
   */
  #isBelowDesktop() {
    return this.#belowDesktopQuery.matches;
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    // CARLYLE (2026-08-01): re-resolve before trusting the cache. This bar is a puppet — it forwards
    // the tap to the real add-to-cart button in the product form — so a stale reference here is a
    // button that looks perfectly normal and silently does nothing.
    //
    // The cache is refreshed on variant:update (see #handleVariantUpdate), but the buy box can be
    // REPLACED wholesale by product-form.js when a switch flips between the sold-out notify-me form
    // and add-to-cart, and the two variant:update consumers have no guaranteed ordering. If this bar
    // runs first it caches a button that the morph is about to discard. Re-querying costs one DOM
    // lookup per tap and removes the whole class of "enabled button, nothing happens".
    const liveTarget = this.#getProductForm()?.querySelector('[ref="addToCartButton"]');
    if (liveTarget) this.#targetAddToCartButton = liveTarget;

    if (!this.#targetAddToCartButton || !this.#targetAddToCartButton.isConnected) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    // The fly-to-cart animation flies the thumbnail to the cart icon. On mobile the
    // thumbnail is hidden (display:none → offsetParent is null), so there's no source
    // to fly from — skip the animation there. The cart still updates normally.
    const productImage = this.refs.productImage;
    if (!cartIcon || !this.refs.addToCartButton || !productImage || productImage.offsetParent === null) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;
    this.#copyPlanState(newStickyAddToCart);
    this.#syncStickyFlag();

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#observeTarget();
      if (this.#isBelowDesktop()) this.#syncVisibility();
      this.#syncSellingPlan();
    });
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * @param {HTMLElement} source - Server-rendered sticky-add-to-cart element
   */
  #copyPlanState(source) {
    for (const key of ['subscription', 'requiresSellingPlan']) {
      const value = source.dataset[key];
      if (value === undefined) delete this.dataset[key];
      else this.dataset[key] = value;
    }
  }

  /**
   * @param {Event} event
   */
  #handlePurchaseOptionChange = (event) => {
    if (this.dataset.subscription === undefined) return;
    const target = event.target;
    if (!(target instanceof Element) || !target.closest(PURCHASE_OPTION_SELECTOR)) return;

    this.#planSyncAnnounce ||= event.isTrusted;
    if (this.#planSyncFrame) cancelAnimationFrame(this.#planSyncFrame);
    this.#planSyncFrame = requestAnimationFrame(() => {
      const announce = this.#planSyncAnnounce;
      this.#planSyncFrame = 0;
      this.#planSyncAnnounce = false;
      if (this.isConnected) this.#syncSellingPlan(announce);
    });
  };

  /**
   * Shows the selected selling plan (name, price, auto-renew) in place of the one-time bar content.
   * @param {boolean} [announce] - Write the new terms to the polite status region
   */
  #syncSellingPlan(announce = false) {
    if (this.dataset.subscription === undefined) return;

    const planId = this.#getSelectedSellingPlanId();
    const plan = planId ? this.#getSellingPlans()[planId] : undefined;
    const before = this.#planTermsKey();

    if (plan) {
      for (const el of this.querySelectorAll('[data-plan-name]')) el.textContent = plan.name;
      for (const el of this.querySelectorAll('[data-plan-price]')) el.textContent = plan.price;
      this.dataset.subscription = 'true';
    } else if (this.dataset.requiresSellingPlan !== 'true') {
      this.dataset.subscription = 'false';
    }

    const status = this.querySelector('[data-plan-status]');
    if (!announce || !status) return;
    if (!this.#isStuck) {
      status.textContent = '';
      return;
    }
    if (before === this.#planTermsKey()) return;
    const terms = this.dataset.subscription === 'true' ? this.querySelector('.sticky-add-to-cart__label-plan') : null;
    status.textContent = terms?.textContent?.trim() ?? '';
  }

  /**
   * @returns {string}
   */
  #planTermsKey() {
    const name = this.querySelector('[data-plan-name]')?.textContent;
    const price = this.querySelector('[data-plan-price]')?.textContent;
    return `${this.dataset.subscription}|${name}|${price}`;
  }

  // No .checked filter: the Subscriptions app blanks every selling_plan input on one-time — see product-form.js #getSellingPlan.
  /**
   * First non-empty selling_plan input for this product.
   * @returns {string | undefined}
   */
  #getSelectedSellingPlanId() {
    const productForm = this.#getProductForm();
    const scope = productForm?.closest('.shopify-section, dialog, product-card') ?? this.closest('.shopify-section');
    if (!scope) return undefined;

    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (scope.querySelectorAll('input[name="selling_plan"]'));
    for (const input of inputs) {
      if (input.disabled || !input.value) continue;
      const owner = input.closest('product-form-component');
      if (owner && owner !== productForm) continue;
      return input.value;
    }
    return undefined;
  }

  /**
   * @returns {Record<string, {name: string, price: string}>}
   */
  #getSellingPlans() {
    const source = this.querySelector('script[data-selling-plans]');
    if (!source?.textContent) return {};
    try {
      return JSON.parse(source.textContent);
    } catch {
      return {};
    }
  }

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#syncStickyFlag();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#syncStickyFlag();
  }

  /**
   * Mirrors bar visibility and variant availability onto <html data-ca-sticky-atc(-available)>.
   */
  #syncStickyFlag() {
    const root = document.documentElement;
    const available = this.dataset.variantAvailable === 'true';
    const availableFlag = available ? 'true' : 'false';
    if (root.dataset.caStickyAtcAvailable !== availableFlag) root.dataset.caStickyAtcAvailable = availableFlag;
    if (this.#isStuck && available) {
      root.dataset.caStickyAtc = 'shown';
    } else {
      delete root.dataset.caStickyAtc;
    }
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
